import { useState, useEffect, useRef, useCallback } from "react";
import * as echarts from 'echarts';
import "../../scss/pages/PrizeSimulation.scss";

function PrizeSimulation({graphType, simulationCount, attendance, settings}) {
	const [chartOptions, setChartOptions] = useState(null);
	const [isRunning, setIsRunning] = useState(false);
	const [progress, setProgress] = useState(0);
	const workerRef = useRef(null);
	const chartRef = useRef(null);
	const chartInstanceRef = useRef(null);

	const initializeWorker = () =>{
		// Initialize worker once
		workerRef.current = new Worker(new URL('../../prizeSimulationWorker.js', import.meta.url), {type: 'module'});
		// Handle messages from worker
		workerRef.current.onmessage = (event) => {
			const { status, data, progress: progressValue, error } = event.data;

			if (status === 'progress') {
				setProgress(progressValue);
			} else if (status === 'success') {
				data.tooltip.position = (pt) => [pt[0], '10%'];
				setChartOptions(data);
				setIsRunning(false);
				setProgress(0);
			} else if (status === 'error') {
				console.error('Simulation error:', error);
				setIsRunning(false);
				setProgress(0);
			}
		};
	}

	useEffect(() => {
		initializeWorker();
		// Terminate worker on unmount
		return () => {
			if (workerRef.current) {
				workerRef.current.terminate();
			}
		};
	}, []);

	// Initialize the chart instance
	useEffect(() => {
		if (chartRef.current) {
			chartInstanceRef.current = echarts.init(chartRef.current);
		}
		return () => chartInstanceRef.current?.dispose();
	}, []);

	// Set options
	const applyOptions = (options) => {
		if (chartInstanceRef.current) {
			chartInstanceRef.current.setOption(options, true);
		}
	};
	// Get current options
	const getChartOptions = () => {
		if (chartInstanceRef.current) {
			return chartInstanceRef.current.getOption();
		}
		return null;
	};

	const startSimulation = (settings) => {
		if (isRunning) {
			cancelSimulation();
		}

		setIsRunning(true);
		setProgress(0);

		// Send settings to worker
		workerRef.current.postMessage({ settings });
	};

	const cancelSimulation = () => {
		if (workerRef.current) {
			// Terminate current worker
			workerRef.current.terminate();
			// Recreate worker for future use
			initializeWorker();
		}

		setIsRunning(false);
		setProgress(0);
	};

	const allPrizes = [];
	if(settings.type === 'scratch'){
		settings.prizes.forEach((prize)=>{
			allPrizes.push(prize);
		});
	}else{
		settings.slices.forEach((slice)=>{
			slice.prizes.forEach((prize)=>{
				allPrizes.push(prize);
			});
		});
	}
	useEffect(() => {
		if(!chartOptions) return;
		applyOptions(chartOptions);
	}, [chartOptions]);

	useEffect(() => {
		if(graphType && attendance?.length && simulationCount){
			const selectedInLedgend = getChartOptions()?.legend?.[0]?.selected ?? {};
			startSimulation({attendance, graphType, simulationCount, settings, allPrizes, selectedInLedgend});
		}else{
			setChartOptions(null);
		}
	}, [graphType, attendance, simulationCount]);

	return (
		<div className="PrizeSimulation">
			{(!graphType || !attendance || !simulationCount) && <div>Missing settings</div>}
			{isRunning && (
				<div className="progressContainer">
					<div>
						<div>Calculating: {progress}%</div>
						<progress value={progress} max={100} />
						<br />
						<button onClick={cancelSimulation} className="btn btn-secondary btn-sm">Cancel</button>
					</div>
				</div>
			)}
			<div ref={chartRef} className="graph" />
		</div>
	);
}

export default PrizeSimulation;
